import { useEffect, useState } from 'react'
import { Truck } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useExpressCompanies } from '@/hooks/useExpressCompany'
import type { EmployeeManualOrder, ManualOrderDeliveryParams } from '@/api/employee-manual-order'

interface ManualOrderDeliveryDialogProps {
  order: EmployeeManualOrder | null
  open: boolean
  isSubmitting?: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: ManualOrderDeliveryParams) => void
}

export function ManualOrderDeliveryDialog({
  order,
  open,
  isSubmitting = false,
  onOpenChange,
  onSubmit,
}: ManualOrderDeliveryDialogProps) {
  const [deliveryCode, setDeliveryCode] = useState('')
  const [deliveryID, setDeliveryID] = useState('')
  const {
    data: expressCompanies = [],
    isLoading: isExpressLoading,
    isError: isExpressError,
  } = useExpressCompanies({ enabled: open })

  useEffect(() => {
    if (!open) {
      setDeliveryCode('')
      setDeliveryID('')
    }
  }, [open])

  const selectedCompany = expressCompanies.find((company) => company.code === deliveryCode)
  const canSubmit = Boolean(selectedCompany && deliveryID.trim() && !isExpressError)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" />
            手动订单发货
          </DialogTitle>
          <DialogDescription>
            {order ? `为订单 ${order.order_id} 填写快递公司和快递单号` : '填写快递公司和快递单号'}
          </DialogDescription>
        </DialogHeader>

        {order && (
          <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm">
            <p className="font-mono">{order.order_id}</p>
            <p className="mt-1 text-muted-foreground">
              {order.real_name} {order.user_phone}
            </p>
          </div>
        )}

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>快递公司</Label>
            <Select
              value={deliveryCode}
              onValueChange={setDeliveryCode}
              disabled={isExpressLoading || isExpressError}
            >
              <SelectTrigger>
                <SelectValue placeholder={isExpressLoading ? '物流公司加载中' : '请选择快递公司'} />
              </SelectTrigger>
              <SelectContent>
                {expressCompanies.map((company) => (
                  <SelectItem key={company.code} value={company.code}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isExpressError && (
              <p className="text-sm text-destructive">物流公司加载失败</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>快递单号</Label>
            <Input
              value={deliveryID}
              onChange={(event) => setDeliveryID(event.target.value)}
              placeholder="请输入快递单号"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            取消
          </Button>
          <Button
            disabled={!canSubmit || isSubmitting}
            onClick={() => {
              if (!selectedCompany) return
              onSubmit({
                delivery_name: selectedCompany.name,
                delivery_id: deliveryID.trim(),
                delivery_code: selectedCompany.code,
              })
            }}
          >
            {isSubmitting ? '发货中...' : '确认发货'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
